
import { useState } from "react";
import { pantryItems as initialItems } from "../data/mockData";

const CATEGORIES = ["All", "Dairy & Eggs", "Vegetables", "Fruits", "Grains", "Meat", "Pantry"];

export default function Pantry() {
  const [items, setItems] = useState(initialItems);
  const [filter, setFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", quantity: "", unit: "pcs", category: "Vegetables", emoji: "🥦" });

  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  const handleAdd = () => {
    if (!form.name || !form.quantity) return;
    if (editId !== null) {
      setItems(items.map((i) => (i.id === editId ? { ...i, ...form, quantity: Number(form.quantity) } : i)));
      setEditId(null);
    } else {
      setItems([...items, { ...form, id: Date.now(), quantity: Number(form.quantity) }]);
    }
    setForm({ name: "", quantity: "", unit: "pcs", category: "Vegetables", emoji: "🥦" });
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({ name: item.name, quantity: item.quantity, unit: item.unit, category: item.category, emoji: item.emoji });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = (id) => setItems(items.filter((i) => i.id !== id));

  return (
    <div className="page">
      <div style={styles.header}>
        <div>
          <h1 className="page-title">🥫 My Pantry</h1>
          <p className="page-subtitle">Everything in your kitchen, at a glance.</p>
        </div>
        <button className="btn-primary" onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ name: "", quantity: "", unit: "pcs", category: "Vegetables", emoji: "🥦" }); }}>
          + Add Ingredient
        </button>
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="card" style={styles.form}>
          <h3 style={styles.formTitle}>{editId ? "Edit Ingredient" : "Add New Ingredient"}</h3>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Emoji</label>
              <input type="text" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} style={{ maxWidth: "60px" }} />
            </div>
            <div>
              <label style={styles.label}>Name</label>
              <input type="text" placeholder="e.g. Tomatoes" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={styles.label}>Quantity</label>
              <input type="number" placeholder="e.g. 4" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div>
              <label style={styles.label}>Unit</label>
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {["pcs", "g", "kg", "ml", "L", "tbsp", "tsp", "cup", "bulb", "bunch"].map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.label}>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
            <button className="btn-primary" onClick={handleAdd}>{editId ? "Update" : "Add Item"}</button>
            <button className="btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Category filters */}
      <div style={styles.filters}>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setFilter(c)} style={{ ...styles.filterBtn, ...(filter === c ? styles.filterActive : {}) }}>
            {c}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statBox}><span style={styles.statNum}>{items.length}</span><span style={styles.statLabel}>Total Items</span></div>
        <div style={styles.statBox}><span style={styles.statNum}>{[...new Set(items.map(i => i.category))].length}</span><span style={styles.statLabel}>Categories</span></div>
        <div style={styles.statBox}><span style={styles.statNum}>{items.filter(i => i.quantity <= 2).length}</span><span style={styles.statLabel}>Low Stock ⚠️</span></div>
      </div>

      {/* Pantry shelf */}
      {filtered.length === 0 ? (
        <div style={styles.empty}>No items in this category yet. Add some! 🌿</div>
      ) : (
        <div style={styles.shelf}>
          {filtered.map((item) => (
            <div key={item.id} className="card" style={{ ...styles.itemCard, ...(item.quantity <= 2 ? styles.lowStock : {}) }}>
              <div style={styles.itemEmoji}>{item.emoji}</div>
              <div style={styles.itemName}>{item.name}</div>
              <div style={styles.itemQty}>{item.quantity} {item.unit}</div>
              <span className="tag" style={{ fontSize: "0.72rem" }}>{item.category}</span>
              {item.quantity <= 2 && <div style={styles.lowBadge}>Low stock</div>}
              <div style={styles.itemActions}>
                <button style={styles.actionBtn} onClick={() => handleEdit(item)}>✏️</button>
                <button style={styles.actionBtn} onClick={() => handleDelete(item.id)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" },
  form: { marginBottom: "1.5rem", background: "#fffdf8" },
  formTitle: { fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#3d2b1a", marginBottom: "1rem" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "0.75rem" },
  label: { display: "block", fontSize: "0.8rem", fontWeight: 700, color: "#9b7b54", marginBottom: "0.3rem" },
  filters: { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" },
  filterBtn: { padding: "0.35rem 0.9rem", borderRadius: "50px", border: "1.5px solid #e0cdb0", background: "#fff9f2", color: "#9b7b54", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", transition: "all 0.2s" },
  filterActive: { background: "#f0e8d8", color: "#6b4f2e", borderColor: "#c4a882" },
  stats: { display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" },
  statBox: { flex: "1", minWidth: "100px", background: "#f0e8d8", borderRadius: "12px", padding: "1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem" },
  statNum: { fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", color: "#6b4f2e", fontWeight: 600 },
  statLabel: { fontSize: "0.8rem", color: "#9b7b54", fontWeight: 700 },
  shelf: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "1rem" },
  itemCard: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "0.35rem", position: "relative", padding: "1.25rem 1rem" },
  lowStock: { borderColor: "#f0c8a0", background: "#fffaf5" },
  itemEmoji: { fontSize: "2.5rem" },
  itemName: { fontFamily: "'Playfair Display', serif", fontSize: "0.95rem", color: "#3d2b1a", fontWeight: 600 },
  itemQty: { fontSize: "0.85rem", color: "#9b7b54", fontWeight: 700 },
  lowBadge: { background: "#fae4cc", color: "#c4a882", borderRadius: "50px", fontSize: "0.72rem", padding: "0.15rem 0.6rem", fontWeight: 700 },
  itemActions: { display: "flex", gap: "0.5rem", marginTop: "0.25rem" },
  actionBtn: { background: "#f0e8d8", border: "none", borderRadius: "8px", padding: "0.3rem 0.5rem", cursor: "pointer", fontSize: "0.85rem" },
  empty: { textAlign: "center", color: "#9b7b54", padding: "3rem", fontStyle: "italic" },
};
